import React, { useState } from "react";
import { X, Layers, Home, Zap, Droplets, Boxes, MoveVertical, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * EngineeringDetails - a "tap to explain" library of buildable construction
 * details, each with a technical SVG drawing + step-by-step build method
 * (Arabic + English). No external services, no cost. Covers wall sections,
 * ribbed (Hordi) roof fabrication, foundations, stairs (with mid-landing),
 * electrical and plumbing/drainage.
 */

const T = (isAr, ar, en) => (isAr ? ar : en);

function WallSVG() {
  return (
    <svg viewBox="0 0 320 150" className="w-full">
      <rect x="20" y="30" width="12" height="90" fill="#CBD5E1" />
      <rect x="32" y="30" width="60" height="90" fill="#94A3B8" />
      <rect x="92" y="30" width="12" height="90" fill="#CBD5E1" />
      <line x1="26" y1="26" x2="26" y2="14" stroke="#1E3A5F" /><text x="20" y="12" fontSize="9" fill="#1E3A5F">2cm</text>
      <line x1="62" y1="26" x2="62" y2="14" stroke="#1E3A5F" /><text x="46" y="12" fontSize="9" fill="#1E3A5F">20cm block</text>
      <line x1="98" y1="26" x2="98" y2="14" stroke="#1E3A5F" /><text x="94" y="12" fontSize="9" fill="#1E3A5F">2cm</text>
      <line x1="20" y1="128" x2="104" y2="128" stroke="#1E3A5F" /><text x="120" y="80" fontSize="11" fontWeight="700" fill="#0F172A">≈ 24 cm total</text>
    </svg>
  );
}

function HordiSVG() {
  return (
    <svg viewBox="0 0 340 170" className="w-full">
      <rect x="10" y="20" width="320" height="18" fill="#CBD5E1" stroke="#94A3B8" />
      <text x="150" y="15" fontSize="9" fill="#1E3A5F">Topping 5–7cm</text>
      {[0, 1, 2, 3].map((i) => (
        <g key={i}>
          <rect x={20 + i * 78} y="38" width="52" height="46" rx="3" fill="#FDE68A" stroke="#CA8A04" />
          <rect x={72 + i * 78} y="38" width="16" height="46" fill="#94A3B8" />
          <circle cx={80 + i * 78} cy="76" r="3" fill="#0F172A" />
        </g>
      ))}
      <rect x="10" y="84" width="320" height="8" fill="#E2E8F0" />
      <text x="30" y="60" fontSize="9" fill="#92400E">Hordi block</text>
      <text x="150" y="105" fontSize="9" fill="#475569">Rib 12–17cm + rebar</text>
      <line x1="10" y1="120" x2="10" y2="150" stroke="#94A3B8" strokeDasharray="3 2" />
      <text x="90" y="140" fontSize="9" fill="#475569">Formwork (shoring) below</text>
    </svg>
  );
}

function FoundationSVG() {
  return (
    <svg viewBox="0 0 320 170" className="w-full">
      <rect x="10" y="120" width="300" height="30" fill="#E7E5E4" />
      <rect x="90" y="110" width="140" height="14" fill="#A8A29E" /><text x="120" y="108" fontSize="9" fill="#1E3A5F">Blinding 5cm</text>
      <rect x="110" y="80" width="100" height="30" fill="#94A3B8" stroke="#475569" />
      <line x1="118" y1="105" x2="202" y2="105" stroke="#0F172A" strokeDasharray="4 2" />
      <line x1="118" y1="98" x2="202" y2="98" stroke="#0F172A" strokeDasharray="4 2" />
      <text x="220" y="98" fontSize="9" fill="#0F172A">Rebar mesh</text>
      <rect x="150" y="30" width="20" height="52" fill="#64748B" stroke="#334155" />
      <text x="176" y="45" fontSize="9" fill="#475569">Column + starter bars</text>
      <text x="120" y="76" fontSize="9" fontWeight="700" fill="#0F172A">Isolated footing</text>
    </svg>
  );
}

function StairSVG() {
  return (
    <svg viewBox="0 0 320 170" className="w-full">
      {[0, 1, 2, 3, 4].map((i) => (
        <rect key={i} x={20 + i * 22} y={120 - i * 16} width="22" height={16} fill="#E2E8F0" stroke="#94A3B8" />
      ))}
      <rect x="130" y="44" width="60" height="14" fill="#CBD5E1" stroke="#64748B" />
      <text x="132" y="40" fontSize="9" fill="#0F172A">Mid-landing (استراحة)</text>
      {[0, 1, 2, 3, 4].map((i) => (
        <rect key={i} x={190 + i * 22} y={44 - 0} width="22" height={16 + i * 0} fill="#E2E8F0" stroke="#94A3B8" transform={`translate(0 ${i * -16})`} />
      ))}
      <text x="30" y="145" fontSize="10" fontWeight="800" fill="#0F172A">UP ↑</text>
      <text x="250" y="30" fontSize="10" fontWeight="800" fill="#0F172A">↑ UP</text>
      <text x="120" y="160" fontSize="9" fill="#475569">Riser ≤ 17cm · Tread ≥ 28cm</text>
    </svg>
  );
}

function ElectricalSVG() {
  return (
    <svg viewBox="0 0 320 150" className="w-full">
      <rect x="20" y="50" width="30" height="45" fill="#1E293B" /><text x="16" y="110" fontSize="9" fill="#475569">DB panel</text>
      <line x1="50" y1="60" x2="300" y2="60" stroke="#F59E0B" strokeWidth="2" />
      <line x1="50" y1="75" x2="300" y2="75" stroke="#0EA5E9" strokeWidth="2" />
      <line x1="50" y1="90" x2="300" y2="90" stroke="#10B981" strokeWidth="2" />
      <text x="200" y="55" fontSize="9" fill="#B45309">Lighting circuit</text>
      <text x="200" y="72" fontSize="9" fill="#0369A1">Sockets 16A</text>
      <text x="200" y="103" fontSize="9" fill="#047857">AC / high-load</text>
      {[100, 160, 220, 280].map((x) => <circle key={x} cx={x} cy="60" r="4" fill="#F59E0B" />)}
    </svg>
  );
}

function PlumbingSVG() {
  return (
    <svg viewBox="0 0 320 150" className="w-full">
      <line x1="20" y1="40" x2="300" y2="40" stroke="#0EA5E9" strokeWidth="3" /><text x="120" y="34" fontSize="9" fill="#0369A1">Supply (PPR)</text>
      <line x1="20" y1="110" x2="300" y2="110" stroke="#64748B" strokeWidth="5" /><text x="110" y="128" fontSize="9" fill="#334155">Drainage 4in (slope 1-2%)</text>
      <line x1="80" y1="40" x2="80" y2="110" stroke="#0EA5E9" strokeWidth="2" />
      <line x1="200" y1="40" x2="200" y2="110" stroke="#0EA5E9" strokeWidth="2" />
      <line x1="140" y1="60" x2="140" y2="20" stroke="#94A3B8" strokeWidth="2" strokeDasharray="4 2" /><text x="146" y="24" fontSize="9" fill="#475569">Vent</text>
      <circle cx="80" cy="110" r="5" fill="#334155" /><circle cx="200" cy="110" r="5" fill="#334155" />
    </svg>
  );
}

function useDetails(isAr) {
  return [
    { id: "wall", icon: Layers, color: "#0EA5E9", title: T(isAr, "الجدران والسماكة", "Walls & thickness"), svg: <WallSVG />,
      steps: isAr
        ? ["جدار خارجي: بلوك 20سم + لياسة 2سم لكل وجه ≈ 24سم إجمالي.", "جدار داخلي: بلوك 10–15سم حسب الوظيفة.", "عزل حراري داخل البلوك المفرّغ للجدران الخارجية.", "الأعمدة والكمرات خرسانية مسلّحة، والجدران غير حاملة (تقسيمية)."]
        : ["Exterior: 20cm block + 2cm plaster each side ≈ 24cm total.", "Interior: 10–15cm block per function.", "Thermal insulation inside hollow block on exterior walls.", "Columns/beams are RC; walls are non-loadbearing partitions."] },
    { id: "roof", icon: Boxes, color: "#CA8A04", title: T(isAr, "الأسقف - هوردي (أعصاب + فلين)", "Roof - Hordi (ribbed) slab"), svg: <HordiSVG />,
      steps: isAr
        ? ["تركيب الشدة (الطبلية) الأفقية وضبط المناسيب والدعامات.", "توزيع بلوكات الهوردي (فلين/طوب مفرّغ) بمسافة صافية للأعصاب 12–17سم.", "تسليح الأعصاب: حديد سفلي + كانات، وربطه مع الكمرات.", "فرش حديد الطبقة العلوية (شبك) بسماكة 5–7سم.", "تمديد الكهرباء والصحي داخل السقف قبل الصب.", "صب خرسانة C25–C30 مع الهزّ، معالجة بالماء 7 أيام، ثم فكّ الشدة بالفترة النظامية."]
        : ["Erect horizontal shoring/formwork, set levels & props.", "Lay Hordi blocks (foam/hollow) leaving 12–17cm clear ribs.", "Reinforce ribs: bottom bars + stirrups, tie into beams.", "Place top mesh, topping 5–7cm.", "Run electrical/plumbing in the slab before pouring.", "Pour C25–C30 with vibration, water-cure 7 days, strike formwork per code."] },
    { id: "foundation", icon: Home, color: "#78716C", title: T(isAr, "الأساسات", "Foundations"), svg: <FoundationSVG />,
      steps: isAr
        ? ["حفر حتى منسوب التأسيس على تربة صالحة (حسب تقرير التربة).", "خرسانة نظافة 5–10سم.", "قواعد منفصلة/شريطية مسلّحة حسب الأحمال والكود المختار.", "أشاير الأعمدة (Starter bars) مربوطة بالقاعدة.", "ميدة (Ground beams) تربط القواعد، ثم الردم والدك."]
        : ["Excavate to founding level on suitable soil (per soil report).", "Blinding concrete 5–10cm.", "RC isolated/strip footings sized to loads & selected code.", "Column starter bars tied into footing.", "Ground beams tie footings, then backfill & compact."] },
    { id: "stairs", icon: MoveVertical, color: "#0F172A", title: T(isAr, "الدرج (مع استراحة وسطية)", "Stairs (with mid-landing)"), svg: <StairSVG />,
      steps: isAr
        ? ["المدخل دائماً من الواجهة الأمامية للمبنى.", "قلبة صاعدة ثم استراحة (بسطة) في المنتصف ثم قلبة ثانية.", "القائمة (Riser) ≤ 17سم والنائمة (Tread) ≥ 28سم.", "عرض الدرج ≥ 100سم، وتُحدَّد جهة الصعود ↑ والنزول ↓ بوضوح."]
        : ["Entrance is always on the building's front facade.", "One flight up, a mid-landing, then a second flight.", "Riser ≤ 17cm, tread ≥ 28cm.", "Stair width ≥ 100cm; UP ↑ and DOWN ↓ clearly marked."] },
    { id: "electrical", icon: Zap, color: "#F59E0B", title: T(isAr, "الكهرباء", "Electrical"), svg: <ElectricalSVG />,
      steps: isAr
        ? ["لوحة توزيع رئيسية (DB) مع قواطع لكل دائرة.", "دوائر منفصلة: إنارة، مقابس 16A، تكييف/أحمال عالية.", "تأريض (Earthing) لكل الدوائر ومساواة جهد.", "المقاطع والقواطع حسب الحمل والكود الكهربائي."]
        : ["Main distribution board (DB) with a breaker per circuit.", "Separate circuits: lighting, 16A sockets, AC/high loads.", "Earthing on all circuits + equipotential bonding.", "Cable sizes & breakers per load and electrical code."] },
    { id: "plumbing", icon: Droplets, color: "#0EA5E9", title: T(isAr, "الصحي والمياه والتصريف", "Plumbing, water & drainage"), svg: <PlumbingSVG />,
      steps: isAr
        ? ["تغذية مياه (بارد/حار) بمواسير PPR من الخزان/العداد.", "صرف صحي 4\" (110مم) بميل 1–2% نحو المنهول.", "تهوية (Vent) لأعلى لمنع كسر السدّ المائي.", "غرف تفتيش (Manholes) عند التقاطعات وتغيّر الاتجاه."]
        : ["Cold/hot water supply in PPR from tank/meter.", "4\" (110mm) drainage at 1–2% slope to the manhole.", "Vent stack to prevent trap siphonage.", "Inspection manholes at junctions & direction changes."] },
  ];
}

export function EngineeringDetailsButton({ isAr, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        data-testid="engineering-details-btn"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 rounded-lg border border-primary/50 bg-primary/10 px-3 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 ${className}`}
      >
        <Layers size={14} /> {T(isAr, "التفاصيل الهندسية · كيف تُبنى؟", "Engineering details · how it's built")}
      </button>
      {open && <EngineeringDetails isAr={isAr} onClose={() => setOpen(false)} />}
    </>
  );
}

export default function EngineeringDetails({ isAr, onClose }) {
  const details = useDetails(isAr);
  const enDetails = useDetails(false);
  const arDetails = useDetails(true);
  const [busy, setBusy] = useState("");

  const exportPdf = async (i) => {
    const en = enDetails[i];
    const arr = arDetails[i];
    setBusy(en.id);
    try {
      await api.editorElementDetailPdf({
        title: en.title,
        subtitle: "Construction detail - how it's built",
        title_ar: arr?.title || "",
        subtitle_ar: "تفصيلة إنشائية - طريقة التنفيذ",
        diagram: en.id,
        sections: [{
          heading: "Build method",
          body: en.steps.join("  •  "),
          heading_ar: "طريقة التنفيذ",
          body_ar: (arr?.steps || []).join("  •  "),
        }],
        code_refs: [],
      });
      toast.success(isAr ? "تم تنزيل بطاقة التفصيلة PDF" : "Detail sheet PDF downloaded");
    } catch (e) {
      toast.error(isAr ? "تعذّر التصدير" : "Export failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="my-6 h-max w-full max-w-3xl rounded-2xl border border-border bg-card p-5" onClick={(e) => e.stopPropagation()} dir={isAr ? "rtl" : "ltr"}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-black">{T(isAr, "التفاصيل الهندسية - قابلة للتنفيذ على أرض الواقع", "Engineering details - buildable on site")}</h2>
          <button data-testid="engineering-details-close" onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full hover:bg-white/10"><X size={16} /></button>
        </div>
        <div className="space-y-4">
          {details.map((d, i) => (
            <div key={d.id} data-testid={`eng-detail-${d.id}`} className="rounded-xl border border-border bg-background/50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: `${d.color}22` }}><d.icon size={16} color={d.color} /></span>
                <p className="text-sm font-black">{d.title}</p>
                <button
                  data-testid={`eng-detail-pdf-${d.id}`}
                  onClick={() => exportPdf(i)}
                  disabled={busy === d.id}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-primary/50 bg-primary/10 px-2.5 py-1 text-[11px] font-bold text-primary hover:bg-primary/20 disabled:opacity-50"
                >
                  <FileDown size={13} /> {busy === d.id ? T(isAr, "جارٍ…", "…") : T(isAr, "تصدير PDF", "Export PDF")}
                </button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg bg-white p-2">{d.svg}</div>
                <ol className="list-inside list-decimal space-y-1 text-xs leading-relaxed text-muted-foreground">
                  {d.steps.map((s, i2) => <li key={i2}>{s}</li>)}
                </ol>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
